import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { TOKENS } from '../tokens';

const SPECS = [
  {
    icon: '📐', key: 'Floor Area' , value: '225', unit: ' sq. yds',
    detail: 'Approximately 1,880 sq. ft. of usable ground coverage — substantial enough for warehouse, office, or manufacturing use.',
  },
  {
    icon: '🏭', key: 'Property Type', value: 'Industrial', unit: ' / Commercial',
    detail: 'Zoned for industrial and commercial activity — giving you maximum operational flexibility from day one.',
  },
  {
    icon: '📍', key: 'Address', value: 'Jhandewalan', unit: ', New Delhi',
    detail: "Central Delhi's established industrial-commercial corridor. Minutes from Karol Bagh, Connaught Place, and the Ring Road. Metro-connected. Freight-accessible.",
  },
  {
    icon: '🔑', key: 'Status', value: 'Available', unit: ' now',
    detail: 'Ready for immediate transaction. No pending disputes. Site visits can be arranged within 24 hours of inquiry.',
  },
];

function BentoCard({ spec, delay }) {
  const [mouse, setMouse] = useState({ x: '50%', y: '50%' });
  const [hovered, setHovered] = useState(false);
  const ref = useRef(null);

  const handleMouseMove = (e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setMouse({
      x: ((e.clientX - r.left) / r.width * 100).toFixed(1) + '%',
      y: ((e.clientY - r.top) / r.height * 100).toFixed(1) + '%',
    });
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay }}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-hover
      style={{
        background: hovered ? '#111C38' : '#0D1535',
        padding: 'clamp(32px,4vw,48px) clamp(24px,3vw,44px)',
        position: 'relative',
        overflow: 'hidden',
        transition: 'background 0.35s',
        cursor: 'none',
      }}
    >
      {/* Mouse glow */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: `radial-gradient(circle at ${mouse.x} ${mouse.y}, rgba(200,169,106,0.07) 0%, transparent 55%)`,
        opacity: hovered ? 1 : 0,
        transition: 'opacity 0.4s',
      }} />

      <span style={{ fontSize: '2rem', marginBottom: 20, display: 'block' }}>{spec.icon}</span>
      <span style={{
        fontSize: '0.65rem', fontWeight: 500, letterSpacing: '0.18em',
        textTransform: 'uppercase', color: TOKENS.MUTED,
        display: 'block', marginBottom: 10,
      }}>{spec.key}</span>
      <span style={{
        fontFamily: "'Cormorant Garamond', serif",
        fontSize: 'clamp(1.8rem, 3vw, 2.6rem)', fontWeight: 300,
        color: '#fff', lineHeight: 1.05,
        display: 'block', marginBottom: 10,
      }}>
        {spec.value}<small style={{ fontSize: '1rem', color: TOKENS.MUTED }}>{spec.unit}</small>
      </span>
      <p style={{
        fontSize: '0.8rem', fontWeight: 300, lineHeight: 1.75,
        color: 'rgba(255,255,255,0.38)', marginTop: 10,
      }}>{spec.detail}</p>

      {/* Gold underline on hover */}
      <div style={{
        position: 'absolute', bottom: 0, left: 'clamp(24px,3vw,44px)',
        height: 2, background: TOKENS.GOLD,
        width: hovered ? 'calc(100% - clamp(48px,6vw,88px))' : 0,
        transition: 'width 0.6s cubic-bezier(0.16,1,0.3,1)',
      }} />
    </motion.div>
  );
}

export default function Details() {
  return (
    <section id="the-details" style={{ background: '#050A1A', padding: 'clamp(72px,10vw,140px) clamp(20px,4vw,48px)' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', maxWidth: 660, margin: '0 auto 72px' }}>
        <ChapterTag text="Chapter 02 · What You're Getting" />
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
          style={secTitleStyle}
        >
          Every detail,<br /><em style={{ fontStyle: 'italic', color: TOKENS.GOLD }}>considered.</em>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
          style={{ fontSize: '1rem', fontWeight: 300, lineHeight: 1.8, color: TOKENS.MUTED, marginTop: 18 }}
        >
          No vague listings. No guesswork. Here is precisely what this property offers.
        </motion.p>
      </div>

      {/* 2×2 grid — no wide cards, no blank spaces, clean on all screens */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 2,
        maxWidth: 1000,
        margin: '0 auto',
        background: '#0A1030',
        borderRadius: 20,
        overflow: 'hidden',
      }}>
        {SPECS.map((spec, i) => (
          <BentoCard key={spec.key} spec={spec} delay={i * 0.1} />
        ))}
      </div>

      <style>{`
        @media (max-width: 560px) {
          #the-details > div:last-child {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
}

export function ChapterTag({ text }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.5 }}
      transition={{ duration: 0.7 }}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 10,
        fontSize: '0.62rem', fontWeight: 500,
        letterSpacing: '0.22em', textTransform: 'uppercase',
        color: '#C8A96A', marginBottom: 20,
      }}
    >
      <span style={{ display: 'inline-block', width: 24, height: 1, background: '#C8A96A' }} />
      {text}
    </motion.div>
  );
}

const secTitleStyle = {
  fontFamily: "'Cormorant Garamond', serif",
  fontSize: 'clamp(2.4rem, 5vw, 4.5rem)',
  fontWeight: 300, lineHeight: 1.1, color: '#fff',
};
